import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scrolls to top on route change. Was a class component using withRouter,
 * which react-router 6 removed.
 *
 * When the location carries a hash it scrolls to that element instead. The
 * browser only does this itself on a full page load, so a client-side
 * navigation to "/#pricing" — which is what /pricing now redirects to — would
 * otherwise land at whatever scroll position the previous route had.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return;
    }

    const id = decodeURIComponent(hash.slice(1));

    /*
     * Corrective second pass.
     *
     * Images between here and the target are lazy-loaded, so they claim their
     * real height as the scroll carries them through the viewport. The browser
     * fixed its destination before that happened, and the target ends up around
     * 80px below where it should sit. Re-aligning once the scroll has settled
     * costs nothing when the first pass was already correct, because the guard
     * below skips it when the element is within a few pixels.
     */
    const settle = () => {
      const el = document.getElementById(id);
      if (!el) return;

      const wanted = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
      const drift = el.getBoundingClientRect().top - wanted;

      if (Math.abs(drift) > 8) {
        window.scrollBy({ top: drift, behavior: 'smooth' });
      }
    };

    // The target belongs to the route that is still rendering on this tick,
    // so wait a frame before looking for it.
    let raf2 = 0;
    let timer = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (!el) return;
        // scroll-margin-top on the sections keeps the heading clear of the
        // sticky header, so no manual offset is needed here.
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        timer = window.setTimeout(settle, 900);
      });
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.clearTimeout(timer);
    };
  }, [pathname, hash]);

  return null;
}
